import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Heading from "@tiptap/extension-heading";
import Blockquote from "@tiptap/extension-blockquote";
import ListItem from "@tiptap/extension-list-item";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Box, IconButton, Tooltip, Paper, Select, MenuItem } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useThemeContext } from "../../context/ThemeContext";

export default function CustomTextArea({ initialContent = "", onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      Heading.configure({ levels: [1, 2, 3,4 ,5, 6] }),
      Blockquote,
      ListItem,
      BulletList,
      OrderedList,
      Link,
      Image,
      TextAlign.configure({ types: ["left", "center", "right", "justify"] }),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  const { mode } = useThemeContext();
  const isDarkMode = mode === "dark";

  if (!editor) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: "8px",
        padding: "16px",
        backgroundColor: isDarkMode ? "#222" : "#fff",
        color: isDarkMode ? "#fff" : "#000",
        width: "100%",
        maxWidth: "600px",
      }}
    >
      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <Select
          value="paragraph"
          onChange={(event) => {
            if (event.target.value === "heading1") editor.chain().focus().toggleHeading({ level: 1 }).run();
            if (event.target.value === "heading2") editor.chain().focus().toggleHeading({ level: 2 }).run();
            if (event.target.value === "heading3") editor.chain().focus().toggleHeading({ level: 3 }).run();
            if (event.target.value === "heading4") editor.chain().focus().toggleHeading({ level: 4 }).run();
            if (event.target.value === "heading5") editor.chain().focus().toggleHeading({ level: 5 }).run();
            if (event.target.value === "heading6") editor.chain().focus().toggleHeading({ level: 6 }).run();
            if (event.target.value === "blockquote") editor.chain().focus().toggleBlockquote().run();
          }}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="paragraph">Paragraph</MenuItem>
          <MenuItem value="blockquote">Quotation</MenuItem>
          <MenuItem value="heading1"><b>Heading 1</b></MenuItem>
          <MenuItem value="heading2"><b>Heading 2</b></MenuItem>
          <MenuItem value="heading3"><b>Heading 3</b></MenuItem>
          <MenuItem value="heading3"><b>Heading 4</b></MenuItem>
          <MenuItem value="heading3"><b>Heading 5</b></MenuItem>
          <MenuItem value="heading3"><b>Heading 6</b></MenuItem>
        </Select>
        <Tooltip title="Bold">
          <IconButton onClick={() => editor.chain().focus().toggleBold().run()}>
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton onClick={() => editor.chain().focus().toggleItalic().run()}>
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <FormatUnderlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet List">
          <IconButton onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <FormatListBulletedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <FormatListNumberedIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <EditorContent
        editor={editor}
        className="p-3 border rounded min-h-[200px]"
        style={{
          backgroundColor: isDarkMode ? "#333" : "#fff",
          color: isDarkMode ? "#fff" : "#000",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid #ccc",
          minHeight: "200px",
        }}
      />
    </Paper>
  );
}